// Espace candidat : « Mes candidatures » et page d'une candidature (logement, foyer et garants, visites, messages,
// dépôt ou retrait, puis sections des autres modules comme « Mes pièces »).
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { del, patch, post, put } from "@core/api";
import { date, dateTime, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { PublicShell } from "@core/layout";
import { extensions } from "@core/modules";
import {
  Button,
  Card,
  Checkbox,
  Empty,
  ErrorBox,
  Fil,
  Input,
  KeyValues,
  Loading,
  Modal,
  MoneyInput,
  Notice,
  Select,
  Textarea,
  fieldError,
  useToast,
} from "@core/ui";
import { Thread, type PersonView } from "./application";
import { AppStatusBadge, CHECKS_EXPLAINED, FORBIDDEN_PIECES, SITUATIONS, parisDateTime, regimeLabel, rentLine } from "./shared";

type MyItem = {
  id: string;
  status: string;
  status_label: string;
  household_label: string;
  created_at: string;
  submitted_at: string | null;
  my_role: string;
  my_role_label: string;
  campaign: { id: string; title: string; status: string; status_label: string; public_slug: string | null; rent_cents: number; charges_cents: number; property_label: string; city: string | null };
  landlord_name: string;
  next_visit_at: string | null;
};

export function MyApplicationsPage() {
  const q = useGet<{ items: MyItem[] }>("/api/v1/me/applications");
  return (
    <PublicShell wide>
      <p className="small">
        <Link to="/moi">← Mon espace personnel</Link>
      </p>
      <h1>Mes candidatures</h1>
      <p className="muted">Vos dossiers de candidature, ceux où vous êtes cotitulaire et ceux où vous vous portez garant. Vous ne voyez jamais les dossiers des autres candidats.</p>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && !q.data.items.length ? (
        <Empty title="Aucune candidature.">Ouvrez le lien de candidature transmis par un bailleur pour constituer votre dossier.</Empty>
      ) : null}
      <ul className="list-plain location-cards">
        {q.data?.items.map((a) => (
          <li key={a.id} className="card">
            <div className="row-between">
              <div>
                <Link to={`/moi/candidatures/${a.id}`}>
                  <strong>{a.campaign.title}</strong>
                </Link>
                <span className="block muted small">
                  {a.campaign.property_label}
                  {a.campaign.city ? ` · ${a.campaign.city}` : ""} · {a.landlord_name}
                </span>
              </div>
              <AppStatusBadge status={a.status} label={a.status_label} />
            </div>
            <p className="small">
              {rentLine(a.campaign.rent_cents, a.campaign.charges_cents)} · Vous êtes {a.my_role_label.toLowerCase()} · {a.submitted_at ? `déposée le ${date(a.submitted_at)}` : `commencée le ${date(a.created_at)}`}
              {a.next_visit_at ? ` · visite le ${parisDateTime(a.next_visit_at, true)}` : ""}
            </p>
          </li>
        ))}
      </ul>
    </PublicShell>
  );
}

type Detail = {
  application: { id: string; status: string; status_label: string; household_label: string; message: string; created_at: string; submitted_at: string | null };
  me: { applicant_id: string; role: string; role_label: string };
  campaign: {
    id: string;
    title: string;
    status: string;
    status_label: string;
    accepting: boolean;
    public_slug: string | null;
    description: string;
    lease_regime: string;
    rent_cents: number;
    charges_cents: number;
    charges_mode: string;
    deposit_cents: number;
    available_on: string | null;
    property: { label: string; city: string | null; postal_code: string | null; surface_m2: number | null; rooms: number | null; dpe_class: string | null };
  };
  landlord_name: string;
  persons: (PersonView & { is_me: boolean; can_edit: boolean; can_remove: boolean; can_invite: boolean })[];
  household: { complete: boolean; tenants_declared_income_cents?: number; guarantors_declared_income_cents?: number; tenants_income_missing?: number };
  problems: string[];
  events: { kind: string; to_label: string | null; created_at: string }[];
  permissions: {
    can_submit: boolean;
    can_withdraw: boolean;
    can_edit_message: boolean;
    can_add_cotitulaire: boolean;
    can_add_guarantor: boolean;
    can_message: boolean;
    can_book_visit: boolean;
    household_editable: boolean;
  };
};

type PersonForm = {
  id?: string;
  role: "cotitulaire" | "garant" | "titulaire";
  guarantee_kind: string;
  guarantor_of: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  situation: string;
  employer_name: string;
  employer_siren: string;
  declared_monthly_income_cents: number | null;
  invite: boolean;
  email_locked?: boolean;
};

function fromPerson(p: PersonView): PersonForm {
  return {
    id: p.id,
    role: p.role as PersonForm["role"],
    guarantee_kind: p.guarantee_kind ?? "personne",
    guarantor_of: p.guarantor_of ?? "",
    first_name: p.first_name ?? "",
    last_name: p.last_name ?? "",
    email: p.email ?? "",
    phone: p.phone ?? "",
    situation: p.situation ?? "autre",
    employer_name: p.employer?.name ?? "",
    employer_siren: p.employer?.siren ?? "",
    declared_monthly_income_cents: p.declared_monthly_income_cents ?? null,
    invite: false,
    email_locked: !!p.account_linked,
  };
}

function stepState(status: string, step: number): "fait" | "actuel" | "a-venir" {
  const order: Record<string, number> = { brouillon: 0, deposee: 1, a_completer: 1, en_verification: 2, verifiee: 2, retenue: 3, refusee: 3, liste_attente: 3, retiree: 3 };
  const at = order[status] ?? 0;
  return step < at ? "fait" : step === at ? "actuel" : "a-venir";
}

export function MyApplicationPage() {
  const { applicationId = "" } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const invalidate = useInvalidate();
  const base = `/api/v1/me/applications/${applicationId}`;
  const q = useGet<Detail>(base);
  const [editing, setEditing] = useState<PersonForm | null>(null);
  const [personError, setPersonError] = useState<unknown>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const sections = extensions("candidateSections");
  const refresh = () => invalidate(base, "/api/v1/me/applications");

  if (q.isLoading) {
    return (
      <PublicShell wide>
        <Loading />
      </PublicShell>
    );
  }
  if (q.error || !q.data) {
    return (
      <PublicShell>
        <h1>Candidature introuvable</h1>
        <ErrorBox error={q.error} />
        <Link to="/moi/candidatures">Mes candidatures</Link>
      </PublicShell>
    );
  }
  const d = q.data;
  const c = d.campaign;
  const isTitulaire = d.me.role === "titulaire";
  const tenants = d.persons.filter((p) => p.role !== "garant");
  const guarantors = d.persons.filter((p) => p.role === "garant");
  const place = [c.property.postal_code, c.property.city].filter(Boolean).join(" ");

  async function savePerson(e: FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setBusy(true);
    setPersonError(null);
    const payload = {
      role: editing.role,
      guarantee_kind: editing.guarantee_kind,
      guarantor_of: editing.guarantor_of || null,
      first_name: editing.first_name,
      last_name: editing.last_name,
      email: editing.email || null,
      phone: editing.phone || null,
      situation: editing.situation,
      employer_name: editing.employer_name,
      employer_siren: editing.employer_siren,
      declared_monthly_income_cents: editing.declared_monthly_income_cents,
      invite: editing.invite,
    };
    try {
      if (editing.id) await put(`${base}/applicants/${editing.id}`, payload);
      else await post(`${base}/applicants`, payload);
      toast(editing.invite ? "Personne ajoutée ; l’invitation est envoyée." : "Informations enregistrées.");
      setEditing(null);
      refresh();
    } catch (err) {
      setPersonError(err);
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    setBusy(true);
    setSubmitError(null);
    try {
      await post(`${base}/submit`, {});
      toast("Dossier transmis au bailleur.");
      refresh();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setBusy(false);
    }
  }

  const personCard = (p: Detail["persons"][number]) => {
    const organism = p.role === "garant" && p.guarantee_kind !== "personne";
    return (
      <div key={p.id} className="location-person">
        <div className="row-between">
          <div>
            <strong>
              {p.display_name || "—"}
              {p.is_me ? " (vous)" : ""}
            </strong>
            <span className="block muted small">
              {p.role_label}
              {p.role === "garant" ? (organism ? ` · ${p.guarantee_kind_label}` : p.guarantor_of_name ? ` de ${p.guarantor_of_name}` : " du foyer") : ""}
              {p.situation_label && !organism ? ` · ${p.situation_label}` : ""}
            </span>
          </div>
          <span className="small">{p.complete ? "Informations renseignées" : "Informations à compléter"}</span>
        </div>
        {p.declared_monthly_income_cents !== undefined && !organism ? (
          <p className="small">
            Revenus mensuels déclarés : {p.declared_monthly_income_cents === null ? "non renseignés" : <span className="num">{money(p.declared_monthly_income_cents)}</span>}
            {p.employer_name ? ` · ${p.employer_name}` : ""}
          </p>
        ) : null}
        {p.guarantee_kind === "visale" ? <p className="small muted">Joignez votre visa Visale à vos pièces. Le bailleur active la garantie après la signature du bail.</p> : null}
        {!organism && !p.is_me ? (
          <p className="small muted">
            {p.account_linked ? "A rattaché son compte et complète sa partie." : p.invitation_sent_at ? `Invitation envoyée le ${date(p.invitation_sent_at)}, en attente.` : "N’a pas rattaché de compte."}
          </p>
        ) : null}
        {p.problems?.length ? <p className="small">À compléter : {p.problems.join(" ")}</p> : null}
        <div className="row">
          {p.can_edit ? (
            <Button small variant="secondaire" onClick={() => setEditing(fromPerson(p))}>
              {p.is_me ? "Compléter mes informations" : "Modifier"}
            </Button>
          ) : null}
          {p.can_invite ? (
            <Button
              small
              variant="discret"
              onClick={async () => {
                try {
                  await post(`${base}/applicants/${p.id}/invite`, {});
                  toast("Invitation envoyée.");
                  refresh();
                } catch (err: any) {
                  toast(err.message, "erreur");
                }
              }}
            >
              {p.invitation_sent_at ? "Renvoyer l’invitation" : "Inviter à compléter sa partie"}
            </Button>
          ) : null}
          {p.can_remove ? (
            <Button
              small
              variant="discret"
              onClick={async () => {
                const dependants = d.persons.filter((x) => x.guarantor_of === p.id).length;
                const text = p.is_me ? "Quitter ce dossier ?" : `Retirer ${p.display_name} du dossier ?${dependants ? " Ses garants seront aussi retirés." : ""}`;
                if (!window.confirm(text)) return;
                try {
                  const r = await del<{ left: boolean }>(`${base}/applicants/${p.id}`);
                  toast(r.left ? "Vous avez quitté le dossier." : "Personne retirée du dossier.");
                  if (r.left) nav("/moi/candidatures");
                  else refresh();
                } catch (err: any) {
                  toast(err.message, "erreur");
                }
              }}
            >
              {p.is_me ? "Quitter le dossier" : "Retirer"}
            </Button>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <PublicShell wide>
      <p className="small">
        <Link to="/moi/candidatures">← Mes candidatures</Link>
      </p>
      <div className="stack">
        <header className="row-between">
          <div>
            <p className="eyebrow">{place || c.property.label}</p>
            <h1>{c.title}</h1>
            <p className="muted">
              Proposé par {c.property.label ? `${d.landlord_name} · ${c.property.label}` : d.landlord_name} · vous êtes {d.me.role_label.toLowerCase()}
            </p>
          </div>
          <AppStatusBadge status={d.application.status} label={d.application.status_label} />
        </header>
        <Fil
          steps={[
            { title: "Constituer le dossier", text: "Foyer, garants et pièces.", state: stepState(d.application.status, 0) },
            { title: "Dossier déposé", text: d.application.submitted_at ? `Le ${date(d.application.submitted_at)}` : "Transmis au bailleur.", state: stepState(d.application.status, 1) },
            { title: "Vérification des pièces", text: "Vous voyez les mêmes constats que le bailleur.", state: stepState(d.application.status, 2) },
            { title: "Décision du bailleur", text: ["retenue", "refusee", "liste_attente", "retiree"].includes(d.application.status) ? d.application.status_label : "Le bailleur décide.", state: stepState(d.application.status, 3) },
          ]}
        />
        {d.application.status === "a_completer" ? (
          <Notice tone="attention" title="Le bailleur vous demande un complément">
            Consultez ses messages et vos pièces, corrigez ce qui est demandé, puis renvoyez votre dossier.
          </Notice>
        ) : null}
        {d.application.status === "retiree" ? <Notice>Vous avez retiré cette candidature. Elle reste consultable ici.</Notice> : null}
        <Notice>{CHECKS_EXPLAINED}</Notice>

        <div className="grid-2">
          <Card title="Le logement">
            <KeyValues
              items={[
                ["Loyer", rentLine(c.rent_cents, c.charges_cents, c.charges_mode)],
                ["Dépôt de garantie", c.deposit_cents ? money(c.deposit_cents) : "Aucun"],
                ["Disponible", c.available_on ? `à partir du ${date(c.available_on)}` : "Dès que possible"],
                ["Type de location", regimeLabel(c.lease_regime)],
                ["Campagne", c.status_label],
              ]}
            />
            {c.public_slug ? (
              <p className="small">
                <Link to={`/candidature/${c.public_slug}`}>Revoir l’annonce</Link>
              </p>
            ) : null}
          </Card>
          <Card title="Votre message au bailleur">
            {d.permissions.can_edit_message ? (
              <form
                className="stack"
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    await patch(base, { message: message ?? d.application.message });
                    toast("Message enregistré.");
                    refresh();
                  } catch (err: any) {
                    toast(err.message, "erreur");
                  }
                }}
              >
                <Textarea label="Présentation (facultatif)" rows={4} value={message ?? d.application.message} onChange={(e) => setMessage(e.target.value)} help="Quelques mots sur votre projet. N’y mettez ni pièce ni information sensible." />
                <Button type="submit" small variant="secondaire">
                  Enregistrer
                </Button>
              </form>
            ) : d.application.message ? (
              <pre className="message-body">{d.application.message}</pre>
            ) : (
              <p className="muted">Aucun message.</p>
            )}
          </Card>
        </div>

        <Card
          title="Foyer"
          actions={
            d.permissions.can_add_cotitulaire ? (
              <Button small variant="secondaire" onClick={() => setEditing({ role: "cotitulaire", guarantee_kind: "personne", guarantor_of: "", first_name: "", last_name: "", email: "", phone: "", situation: "cdi", employer_name: "", employer_siren: "", declared_monthly_income_cents: null, invite: true })}>
                Ajouter un cotitulaire
              </Button>
            ) : null
          }
        >
          <p className="small muted">
            Couple ou colocation : chaque personne déclare ses propres revenus, sans y ajouter ceux des autres. Une personne invitée complète elle-même sa partie. {isTitulaire ? "Vous déposez le dossier commun." : "Le titulaire dépose le dossier commun ; vous voyez la composition du foyer, pas les revenus des autres."}
          </p>
          {tenants.map(personCard)}
          {isTitulaire && d.household.tenants_declared_income_cents !== undefined ? (
            <p className="small">
              Revenus mensuels déclarés du foyer : <strong className="num">{money(d.household.tenants_declared_income_cents)}</strong>
            </p>
          ) : null}
        </Card>

        <Card
          title="Garants"
          actions={
            d.permissions.can_add_guarantor ? (
              <Button
                small
                variant="secondaire"
                onClick={() =>
                  setEditing({ role: "garant", guarantee_kind: "personne", guarantor_of: isTitulaire ? "" : d.me.applicant_id, first_name: "", last_name: "", email: "", phone: "", situation: "cdi", employer_name: "", employer_siren: "", declared_monthly_income_cents: null, invite: true })
                }
              >
                Ajouter un garant
              </Button>
            ) : null
          }
        >
          <p className="small muted">Un garant peut être une personne, un visa Visale ou un organisme de caution. Une personne garante est invitée à confirmer elle-même sa participation ; son engagement n’est jamais présumé.</p>
          {guarantors.length ? guarantors.map(personCard) : <p className="muted">Aucun garant déclaré.</p>}
        </Card>

        <VisitsSection applicationId={applicationId} canBook={d.permissions.can_book_visit} />
        {d.permissions.can_message || d.me.role !== "garant" ? <MessagesSection applicationId={applicationId} landlord={d.landlord_name} /> : null}

        {sections.map((s) => (
          <section key={s.id} aria-label={s.label}>
            <s.Component applicationId={applicationId} />
          </section>
        ))}

        <Card title={d.application.status === "a_completer" ? "Renvoyer le dossier" : "Déposer ou retirer"}>
          {isTitulaire ? (
            <div className="stack">
              {d.problems.length ? (
                <Notice tone="attention" title="Avant de déposer">
                  <ul>
                    {d.problems.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </Notice>
              ) : null}
              {d.persons.some((p) => p.role !== "titulaire" && p.guarantee_kind === "personne" && !p.account_linked) && d.permissions.can_submit ? (
                <p className="small muted">Certaines personnes n’ont pas encore rattaché leur compte. Vous pouvez déposer ; le bailleur verra que leur participation n’est pas encore confirmée.</p>
              ) : null}
              <ErrorBox error={submitError} />
              <div className="row">
                {d.permissions.can_submit ? (
                  <Button busy={busy} onClick={submit}>
                    {d.application.status === "a_completer" ? "Renvoyer mon dossier" : "Déposer mon dossier"}
                  </Button>
                ) : d.application.status === "brouillon" && !c.accepting ? (
                  <p className="muted">Cette campagne ne reçoit plus de candidatures.</p>
                ) : null}
                {d.permissions.can_withdraw ? (
                  <Button variant="discret" onClick={() => setWithdrawing(true)}>
                    Retirer ma candidature
                  </Button>
                ) : null}
              </div>
              <p className="small muted">{FORBIDDEN_PIECES}</p>
            </div>
          ) : (
            <p className="muted">Le titulaire du dossier ({tenants.find((p) => p.role === "titulaire")?.display_name}) dépose ou retire la candidature commune.</p>
          )}
          {d.events.length ? (
            <ol className="list-plain location-timeline">
              {d.events.map((e, i) => (
                <li key={i} className="small">
                  {e.kind === "creee" ? "Candidature commencée" : `Statut : ${e.to_label}`} · {dateTime(e.created_at)}
                </li>
              ))}
            </ol>
          ) : null}
        </Card>
      </div>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? (editing.role === "titulaire" ? "Vos informations" : "Modifier les informations") : editing?.role === "garant" ? "Ajouter un garant" : "Ajouter un cotitulaire"}
        footer={
          <>
            <Button variant="discret" onClick={() => setEditing(null)}>
              Annuler
            </Button>
            <Button type="submit" form="location-personne" busy={busy}>
              Enregistrer
            </Button>
          </>
        }
      >
        {editing ? (
          <form id="location-personne" className="stack" onSubmit={savePerson}>
            <ErrorBox error={personError} />
            {editing.role === "garant" ? (
              <Select
                label="Type de garantie"
                value={editing.guarantee_kind}
                error={fieldError(personError, "guarantee_kind")}
                options={[
                  { value: "personne", label: "Une personne se porte garante" },
                  { value: "visale", label: "Visa Visale (Action Logement)" },
                  { value: "organisme", label: "Autre organisme de caution" },
                ]}
                onChange={(e) => setEditing({ ...editing, guarantee_kind: e.target.value, invite: e.target.value === "personne" && editing.invite })}
              />
            ) : null}
            {editing.role === "garant" && editing.guarantee_kind === "personne" ? (
              <Select
                label="Garant de"
                value={editing.guarantor_of}
                error={fieldError(personError, "guarantor_of")}
                options={[{ value: "", label: "Tout le foyer" }, ...tenants.map((t) => ({ value: t.id, label: t.display_name || t.role_label }))]}
                onChange={(e) => setEditing({ ...editing, guarantor_of: e.target.value })}
              />
            ) : null}
            {editing.guarantee_kind === "visale" && editing.role === "garant" ? (
              <Notice>Le visa Visale se joint à vos pièces. Il ne constitue pas une garantie active : le bailleur l’active après la signature du bail.</Notice>
            ) : editing.guarantee_kind === "organisme" && editing.role === "garant" ? (
              <Input label="Nom de l’organisme" required value={editing.first_name} error={fieldError(personError, "first_name")} onChange={(e) => setEditing({ ...editing, first_name: e.target.value })} />
            ) : (
              <>
                <div className="form-row">
                  <Input label="Prénom" required value={editing.first_name} error={fieldError(personError, "first_name")} onChange={(e) => setEditing({ ...editing, first_name: e.target.value })} />
                  <Input label="Nom" required value={editing.last_name} error={fieldError(personError, "last_name")} onChange={(e) => setEditing({ ...editing, last_name: e.target.value })} />
                </div>
                <div className="form-row">
                  <Input
                    label="Adresse e-mail"
                    type="email"
                    value={editing.email}
                    disabled={editing.email_locked}
                    error={fieldError(personError, "email")}
                    onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                    help={editing.email_locked ? "Adresse du compte rattaché." : "Pour l’inviter à compléter sa partie."}
                  />
                  <Input label="Téléphone (facultatif)" type="tel" value={editing.phone} error={fieldError(personError, "phone")} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} />
                </div>
                <div className="form-row">
                  <Select label="Situation professionnelle" value={editing.situation} options={SITUATIONS} error={fieldError(personError, "situation")} onChange={(e) => setEditing({ ...editing, situation: e.target.value })} />
                  <Input label="Employeur, activité ou établissement" value={editing.employer_name} error={fieldError(personError, "employer_name")} onChange={(e) => setEditing({ ...editing, employer_name: e.target.value })} />
                </div>
                <div className="form-row">
                  <MoneyInput
                    label="Revenus mensuels nets déclarés"
                    cents={editing.declared_monthly_income_cents}
                    error={fieldError(personError, "declared_monthly_income_cents")}
                    help="Vos propres revenus, sans ceux des autres personnes. Indiquez 0 si vous n’en avez pas. Ils seront rapprochés de vos pièces."
                    onChange={(v) => setEditing({ ...editing, declared_monthly_income_cents: v })}
                  />
                  <Input label="SIREN de l’employeur ou de votre activité (facultatif)" inputMode="numeric" value={editing.employer_siren} error={fieldError(personError, "employer_siren")} onChange={(e) => setEditing({ ...editing, employer_siren: e.target.value })} />
                </div>
                {!editing.id || (!editing.email_locked && editing.role !== "titulaire") ? (
                  <Checkbox
                    label="Inviter cette personne par e-mail à compléter sa partie"
                    help="Elle rattache son compte avec cette adresse et dépose ses propres pièces. Rien ne l’engage à ce stade."
                    checked={editing.invite}
                    onChange={(v) => setEditing({ ...editing, invite: v })}
                  />
                ) : null}
              </>
            )}
          </form>
        ) : null}
      </Modal>

      <Modal
        open={withdrawing}
        onClose={() => setWithdrawing(false)}
        title="Retirer ma candidature"
        footer={
          <>
            <Button variant="discret" onClick={() => setWithdrawing(false)}>
              Garder ma candidature
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                try {
                  await post(`${base}/withdraw`, { reason });
                  toast("Candidature retirée. Le bailleur est prévenu.");
                  setWithdrawing(false);
                  refresh();
                } catch (err: any) {
                  toast(err.message, "erreur");
                }
              }}
            >
              Retirer ma candidature
            </Button>
          </>
        }
      >
        <div className="stack">
          <p>Le bailleur est prévenu et votre éventuelle visite est annulée. Un retrait ne peut pas être annulé ; vous pourrez déposer une nouvelle candidature tant que la campagne est ouverte.</p>
          <Textarea label="Motif (facultatif)" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
      </Modal>
    </PublicShell>
  );
}

// ---------------------------------------------------------------------------
// Visites

type CandidateSlot = { id: string; label: string; starts_at: string; remaining: number; full: boolean; mine: boolean; location: string | null; notes: string | null };
type CandidateBooking = { id: string; slot_id: string; status: string; attendance: string | null; cancelled_by: string | null; cancel_reason: string; starts_at: string; location: string | null; can_cancel: boolean };

function VisitsSection({ applicationId, canBook }: { applicationId: string; canBook: boolean }) {
  const toast = useToast();
  const invalidate = useInvalidate();
  const base = `/api/v1/me/applications/${applicationId}/visits`;
  const q = useGet<{ slots: CandidateSlot[]; bookings: CandidateBooking[]; can_book: boolean }>(base);
  const refresh = () => invalidate(base, "/api/v1/me/applications");
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const active = q.data.bookings.find((b) => b.status === "confirmee");
  const history = q.data.bookings.filter((b) => b.status !== "confirmee");
  return (
    <Card title="Visite du logement">
      <p className="small muted">Horaires en heure de Paris. Vous pouvez changer de créneau ou annuler pour libérer la place.</p>
      {active ? (
        <Notice tone="succes" title="Votre visite">
          {parisDateTime(active.starts_at)}
          {active.location ? <span className="block">Lieu : {active.location}</span> : null}
          {active.can_cancel ? (
            <span className="block">
              <Button
                small
                variant="discret"
                onClick={async () => {
                  if (!window.confirm("Annuler cette visite ?")) return;
                  try {
                    await post(`${base}/${active.id}/cancel`, {});
                    toast("Visite annulée. Le bailleur est prévenu.");
                    refresh();
                  } catch (err: any) {
                    toast(err.message, "erreur");
                  }
                }}
              >
                Annuler ma visite
              </Button>
            </span>
          ) : null}
        </Notice>
      ) : null}
      {q.data.slots.length ? (
        <ul className="list-plain location-slots">
          {q.data.slots.map((s) => (
            <li key={s.id} className="row-between">
              <span>
                {s.label}
                <span className="block muted small">{s.mine ? "Votre créneau" : s.full ? "Complet" : `${s.remaining} place${s.remaining > 1 ? "s" : ""} disponible${s.remaining > 1 ? "s" : ""}`}</span>
              </span>
              {canBook && !s.mine && !s.full ? (
                <Button
                  small
                  variant="secondaire"
                  onClick={async () => {
                    try {
                      await post(base, { slot_id: s.id });
                      toast(active ? "Créneau modifié. Votre précédente réservation est annulée." : "Visite confirmée.");
                      refresh();
                    } catch (err: any) {
                      toast(err.message, "erreur");
                      refresh();
                    }
                  }}
                >
                  {active ? "Choisir ce créneau" : "Réserver"}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">Aucun créneau proposé pour l’instant. Vous serez prévenu dès que le bailleur en ajoute ; vous pouvez aussi lui écrire.</p>
      )}
      {history.length ? (
        <details>
          <summary className="small">Historique des visites</summary>
          <ul className="list-plain small location-timeline">
            {history.map((b) => (
              <li key={b.id}>
                {parisDateTime(b.starts_at, true)} · annulée par {b.cancelled_by === "bailleur" ? "le bailleur" : "vous"}
                {b.cancel_reason ? ` (${b.cancel_reason})` : ""}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Messages

function MessagesSection({ applicationId, landlord }: { applicationId: string; landlord: string }) {
  const toast = useToast();
  const invalidate = useInvalidate();
  const base = `/api/v1/me/applications/${applicationId}/messages`;
  const q = useGet<{ items: any[]; recipient: { name: string; campaign_title: string }; can_post: boolean }>(base);
  const [body, setBody] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return null;
  return (
    <Card title="Messages avec le bailleur">
      <Thread items={q.data.items} empty="Aucun message pour l’instant." />
      {q.data.can_post ? (
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              await post(base, { body });
              setBody("");
              toast("Message envoyé.");
              invalidate(base);
            } catch (err) {
              setError(err);
            } finally {
              setBusy(false);
            }
          }}
        >
          <ErrorBox error={error} />
          <Textarea
            label={`Écrire à ${landlord}`}
            rows={4}
            value={body}
            error={fieldError(error, "body")}
            onChange={(e) => setBody(e.target.value)}
            help={`À propos de « ${q.data.recipient.campaign_title} ». Pour transmettre une pièce, utilisez la section de vos pièces plutôt qu’un message.`}
          />
          <div className="form-actions">
            <Button type="submit" small busy={busy} disabled={!body.trim()}>
              Envoyer
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}
